import type React from "react";
import type {PushOptions} from "../core/types";
import {SERVICES} from "../../common/constants/routes";
import {getActiveInteractionId} from "../context/TrackingContext";
import {DEFAULT_ELEMENT_LOCATION, ELEMENT_LOCATION_RULES} from "tracking/core/rules";
import {getLinkTrackingInfo} from "tracking/utils/linkClick";
import {dataLayerCallNumber, dataLayerDownload, dataLayerEmail, dataLayerOutbound} from "tracking/events/links";
import {dataLayerButtonClick} from "tracking/events/buttons";

export type TrackedLinkParams = {
    href: string | null | undefined;
    linkTarget: "_self" | "_blank";
    onClick?: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
    delayMs?: number;
    isExternalOverride?: boolean;
    pushOptions?: PushOptions;
    elementLocation?: string;
    contentLabel?: string | null;
};

function deriveElementLocation(el: Element): string {
    if (!(el instanceof HTMLElement)) {
        return DEFAULT_ELEMENT_LOCATION;
    }

    const matchedRule = ELEMENT_LOCATION_RULES.find(({selector}) =>
        el.closest(selector)
    );

    return matchedRule?.location ?? DEFAULT_ELEMENT_LOCATION;
}


function getEffectivePushOptions(pushOptions?: PushOptions): PushOptions | undefined {
    if (pushOptions) return pushOptions;

    const eventGroupId = typeof getActiveInteractionId === "function" ? getActiveInteractionId() :
        undefined;

    return eventGroupId ? ({eventGroupId} as PushOptions) : undefined;
}

function navigateToUrl(
    href: string,
    linkTarget: "_self" | "_blank",
    delayMs: number
) {
    if (typeof window === "undefined") return;

    window.setTimeout(() => {
        if (linkTarget === "_blank") {
            window.open(href, "_blank", "noopener,noreferrer");
            return;
        }

        window.location.assign(href);
    }, delayMs);
}

/**
 * Click handler that ensures tracking is executed before navigation.
 *
 * - Determines link type (tel, outbound, internal)
 * - Sends the appropriate tracking event
 * - Controls navigation timing for outbound links
 */
export function createLinkClickHandler({
                                           href,
                                           linkTarget,
                                           onClick,
                                           delayMs = 250,
                                           isExternalOverride,
                                           pushOptions,
                                           contentLabel,
                                       }: TrackedLinkParams) {
    return (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        onClick?.(e);

        // Verhindert der Consumer den Default, übernimmt er den Klick selbst und die Navigation zum
        // href findet nicht statt (MenuItemComponent klappt ein Untermenü auf, TopNavi erzwingt
        // einen eigenen Reload). Ein Tracking-Event für eine Navigation, die es nicht gibt, wäre
        // falsch -- deshalb hier abbrechen, bevor irgendein Event erzeugt wird.
        if (e.defaultPrevented) return;

        const result = getLinkTrackingInfo(href, {
            base: typeof window !== "undefined" ? window.location.origin : undefined,
            isExternalOverride,
        });

        const {url, isHttp, isOutbound, hostname, href: rawHref} = result;

        if (!url) return;

        // tel:/mailto: werden aus dem rohen href gelesen, nicht aus url.href: der
        // URL-Konstruktor prozent-kodiert Nicht-ASCII-Zeichen, wodurch etwa der
        // Gedankenstrich in "089/12 606 - 0" als "%E2%80%93" im dataLayer landen
        // würde. Getrackt werden soll die menschenlesbare Nummer/Adresse.
        if (url.protocol === "tel:") {
            dataLayerCallNumber(rawHref.replace(/^tel:/, ""),
                getEffectivePushOptions(pushOptions));
            return;
        }

        if (url.protocol === "mailto:") {
            dataLayerEmail(rawHref.replace(/^mailto:/, ""), getEffectivePushOptions(pushOptions));
            return;
        }

        if (!isHttp) return;

        const link = e.currentTarget;
        const text = link.textContent?.trim() || undefined;

        if (isOutbound) {
            const linkInfo = link.getAttribute("data-info") ?? undefined;
            dataLayerOutbound(
                hostname,
                linkInfo,
                url.href,
                getEffectivePushOptions(pushOptions)
            );

            if (!e.defaultPrevented) {
                e.preventDefault();
                const effectiveDelay = linkTarget === "_blank" ? 0 : delayMs;
                navigateToUrl(url.href, linkTarget, effectiveDelay);
            }
            return;
        }

        if (url.pathname.startsWith(SERVICES.DOWNLOAD) || url.href.includes(":CMDownload")) {
            dataLayerDownload(contentLabel ?? text, url.href);
            return;
        }

        dataLayerButtonClick(
            text,
            deriveElementLocation(link),
            url.href,
            getEffectivePushOptions(pushOptions)
        );
    };
}